import config from './config';

/**
 * Service for handling avatar images with S3 storage (OPTIMIZED)
 */
class AvatarService {
  constructor() {
    // API URL should be configurable for different environments
    this.apiBaseUrl = config.apiUrl;
    
    // OPTIMIZATION: Client-side URL cache
    this.urlCache = new Map();
    this.cacheExpiry = 50 * 60 * 1000; // 50 minutes (before S3 expiry)
    
    // OPTIMIZATION: Preload common avatars on startup
    this.initializeCache();
  }

  /**
   * OPTIMIZATION: Initialize cache with common avatars
   */
  async initializeCache() {
    try {
      // Preload first 20 avatars (most commonly used)
      await this.preloadAvatarUrls(Array.from({length: 20}, (_, i) => `${i + 1}.png`));
    } catch (error) {
      console.warn('🔄 Avatar preloading failed (non-critical):', error.message);
    }
  }

  /**
   * OPTIMIZATION: Pre-warm cache for common avatars
   * @param {Array<string>} keys - Array of avatar keys to preload
   */
  async preloadAvatarUrls(keys) {
    try {
      // Use batch endpoint for efficient preloading
      const start = 1;
      const end = Math.min(20, keys.length);
      
      const response = await fetch(`${this.apiBaseUrl}/avatars/batch/${start}/${end}`);
      if (response.ok) {
        const data = await response.json();
        
        // Cache all URLs
        Object.entries(data.urls).forEach(([key, url]) => {
          this.urlCache.set(key, {
            url,
            timestamp: Date.now()
          });
        });
        
        console.log(`✅ Preloaded ${Object.keys(data.urls).length} avatar URLs`);
      }
    } catch (error) {
      console.warn('Failed to preload avatar URLs:', error.message);
    }
  }

  /**
   * Gets a presigned URL for an existing avatar (OPTIMIZED with caching)
   * @param {string} key - S3 key of the avatar
   * @returns {Promise<string>} - Presigned URL
   */
  async getAvatarUrl(key) {
    // OPTIMIZATION: Check cache first
    const cached = this.urlCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      console.log(`🎯 Using cached URL for ${key}`);
      return cached.url;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/avatar/${key}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get avatar URL');
      }

      const data = await response.json();
      
      // OPTIMIZATION: Cache the URL
      this.urlCache.set(key, {
        url: data.url,
        timestamp: Date.now()
      });
      
      console.log(`⚡ Fetched and cached URL for ${key}`);
      return data.url;
    } catch (error) {
      console.error('Get avatar URL error:', error);
      throw error;
    }
  }

  /**
   * Uploads an avatar image to S3
   * @param {string} base64Image - Base64 encoded image (without the data URL prefix)
   * @param {string} contentType - MIME type of the image
   * @returns {Promise<Object>} - Object containing the S3 key and presigned URL
   */
  async uploadAvatar(base64Image, contentType = 'image/jpeg') {
    try {
      const response = await fetch(`${this.apiBaseUrl}/upload-avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          image: base64Image,
          contentType 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload avatar');
      }

      return await response.json();
    } catch (error) {
      console.error('Avatar upload error:', error);
      throw error;
    }
  }

  /**
   * Deletes an avatar from S3
   * @param {string} key - S3 key of the avatar
   * @returns {Promise<Object>} - Response data
   */
  async deleteAvatar(key) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/avatar/${key}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete avatar');
      }

      // Remove from cache
      this.urlCache.delete(key);

      return await response.json();
    } catch (error) {
      console.error('Delete avatar error:', error);
      throw error;
    }
  }

  /**
   * Helper method to extract base64 data from data URL
   * @param {string} dataUrl - Data URL (e.g., data:image/jpeg;base64,...)
   * @returns {Object} - Object containing base64 data and content type
   */
  extractBase64FromDataUrl(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string') {
      throw new Error('Invalid data URL');
    }

    const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!matches || matches.length !== 3) {
      throw new Error('Invalid data URL format');
    }

    return {
      contentType: matches[1],
      base64Data: matches[2]
    };
  }

  /**
   * Processes a file input and returns a data URL
   * @param {File} file - The file object from file input
   * @returns {Promise<string>} - Data URL
   */
  readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * OPTIMIZED: Gets a random avatar (for new cards only)
   * @param {string} specificKey - Optional specific avatar key to use (for cached cards)
   * @returns {Promise<Object>} - Object containing key and url
   */
  async getRandomAvatar(specificKey = null) {
    try {
      // OPTIMIZATION: If a specific key is provided, use cached URL if available
      if (specificKey) {
        console.log(`🔑 Using specific avatar key: ${specificKey}`);
        const url = await this.getAvatarUrl(specificKey);
        
        return {
          key: specificKey,
          url: url
        };
      }
      
      // OPTIMIZATION: For new cards, generate random number locally
      console.log("🎲 Generating random avatar for new card");
      
      // Choose random number between 1-51
      const randomAvatarNumber = Math.floor(Math.random() * 51) + 1;
      const avatarKey = `${randomAvatarNumber}.png`;
      
      console.log(`🎯 Selected random avatar: ${avatarKey}`);
      
      // Get the URL (will use cache if available)
      const url = await this.getAvatarUrl(avatarKey);
      
      return {
        key: avatarKey,
        url: url
      };
    } catch (error) {
      console.error('Failed to get avatar:', error);
      throw error;
    }
  }

  /**
   * OPTIMIZATION: Clear expired cache entries
   */
  clearExpiredCache() {
    const now = Date.now();
    for (const [key, cached] of this.urlCache.entries()) {
      if (now - cached.timestamp >= this.cacheExpiry) {
        this.urlCache.delete(key);
      }
    }
  }

  /**
   * OPTIMIZATION: Get cache statistics for debugging
   */
  getCacheStats() {
    return {
      size: this.urlCache.size,
      keys: Array.from(this.urlCache.keys()),
      oldestEntry: Math.min(...Array.from(this.urlCache.values()).map(c => c.timestamp)),
      newestEntry: Math.max(...Array.from(this.urlCache.values()).map(c => c.timestamp))
    };
  }
}

const avatarService = new AvatarService();
export default avatarService; 